import Link from "next/link";
import { ArrowUpRight, BriefcaseBusiness, MapPin, Timer } from "lucide-react";

import type { PublicJob } from "@/lib/job-board/types";
import { jobUrl } from "@/lib/job-board/url";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

function experienceLabel(experience: PublicJob["experience"]): string | null {
  const { min, max } = experience;
  if (min === undefined && max === undefined) return null;
  if (min !== undefined && max !== undefined) return `${min}–${max} years`;
  return min !== undefined ? `${min}+ years` : `Up to ${max} years`;
}

function employmentLabel(jobType: string): string {
  return jobType.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

export default function RecentJobCard({ job }: { job: PublicJob }) {
  const experience = experienceLabel(job.experience);
  const url = jobUrl(job.title, job.id);
  const avatarInitial = job.title.trim().charAt(0).toUpperCase() || "J";

  return (
    <article className="recent-job-card flex min-h-[260px] h-full flex-col p-5 motion-reduce:transition-none">
      <header className="flex items-center gap-3">
        <Avatar className="h-10 w-10 rounded-md bg-secondary text-primary">
          <AvatarFallback className="rounded-md bg-secondary font-heading font-semibold text-primary">{avatarInitial}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="mb-0 mt-0.5 text-[11px] text-muted-foreground">Public job opportunity</p>
        </div>
      </header>

      <div className="mt-5">
        <Link
          href={url}
          className="text-job-title line-clamp-2 rounded-sm no-underline outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {job.title}
        </Link>
      </div>

      <ul className="m-0 mt-5 grid list-none grid-cols-1 gap-x-4 gap-y-2 p-0 text-xs text-muted-foreground sm:grid-cols-2">
        <li className="flex min-w-0 items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate">{job.location || "Location not specified"}</span>
        </li>
        {job.jobType ? (
          <li className="flex min-w-0 items-center gap-1.5">
            <BriefcaseBusiness className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
            <span className="truncate">{employmentLabel(job.jobType)}</span>
          </li>
        ) : null}
        {experience ? (
          <li className="flex min-w-0 items-center gap-1.5">
            <Timer className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
            <span className="truncate">{experience}</span>
          </li>
        ) : null}
      </ul>

      <footer className="mt-auto flex items-center justify-end border-t border-border pt-4">
        <Link
          href={url}
          className="inline-flex min-h-6 items-center gap-1 rounded-sm text-xs font-semibold text-primary no-underline outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={`View job: ${job.title}`}
        >
          View Job
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </footer>
    </article>
  );
}
